import { useDisclosure } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AddressComponents } from "../../../../../public/admin/scripts/messages/address_component";
import {
  buildAddressComponentsFromCustomAddress,
  buildCustomAddressTextString,
  getDefaultCustomAddress,
  parseAddress,
} from "../../../shared/components/AddressPicker/address-picker-utils";
import AddressPickerModal, {
  CustomAddress,
} from "../../../shared/components/AddressPicker/AddressPickerModal";

/**
 * The address shape the AngularJS visit instance modal keeps in its `generalForm`
 * and sends to `PUT /visit_instances/:visitInstanceId`, i.e. the server's
 * `VisitInstanceAddress` message.
 */
export interface VisitInstanceCustomAddress {
  address: string;
  timezone: string;
  state: string;
  countyName: string;
  addressComponents: AddressComponents | null;
}

const DEFAULT_TIMEZONE = "America/New_York";

interface Props {
  address: VisitInstanceCustomAddress | null;
  disclosure: ReturnType<typeof useDisclosure>;
  onSave: (address: VisitInstanceCustomAddress) => void;
}

function toVisitInstanceAddress(
  customAddress: CustomAddress,
  fallbackTimezone: string
): VisitInstanceCustomAddress {
  return {
    address: buildCustomAddressTextString(customAddress),
    timezone: customAddress.timezone ?? fallbackTimezone,
    state: customAddress.state,
    countyName: customAddress.county,
    addressComponents: buildAddressComponentsFromCustomAddress(customAddress),
  };
}

/**
 * Lets the visit instance modal edit its address by hand instead of through the
 * google places autocomplete. Nothing is persisted here - the picked address is
 * handed back to the AngularJS form, which saves it along with the rest of the
 * general form.
 */
const VisitInstanceAddressPicker = (props: Props) => {
  const addressQuery = useQuery({
    queryKey: ["visitInstanceCustomAddress", props.address],
    queryFn: (): CustomAddress => {
      if (props.address === null) {
        return getDefaultCustomAddress();
      }

      return {
        ...parseAddress(props.address.address, props.address.addressComponents),
        county: props.address.countyName,
        timezone: props.address.timezone,
      };
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: (customAddress: CustomAddress) => {
      props.onSave(
        toVisitInstanceAddress(customAddress, props.address?.timezone ?? DEFAULT_TIMEZONE)
      );
      return Promise.resolve() as Promise<never>;
    },
    onSuccess: () => props.disclosure.onClose(),
  });

  return (
    <AddressPickerModal
      addressQuery={addressQuery}
      disclosure={props.disclosure}
      updateAddressMutation={updateAddressMutation}
    />
  );
};

export default VisitInstanceAddressPicker;
